import React, { useState, useEffect } from 'react';
import { Phone, Mail, Building2, DollarSign, Calendar, Search, RefreshCw, Check, Clock, X, Package, User, PhoneCall, TrendingUp, AlertCircle } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import './ConsultationRequests.css';

const ConsultationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/requests/consultations');
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load consultation requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/requests/consultations/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesFilter = filter === 'all' || req.status === filter;
    const matchesSearch = 
      req.name_designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.company_place?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.mobile_number?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-warning', icon: Clock, text: 'Pending' },
      contacted: { class: 'badge-info', icon: Phone, text: 'Contacted' },
      completed: { class: 'badge-success', icon: Check, text: 'Completed' },
      cancelled: { class: 'badge-danger', icon: X, text: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`badge ${badge.class}`}>
        <Icon size={14} />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const time = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return { day, time };
  };

  if (loading) {
    return (
      <div className="loading-state">
        <RefreshCw className="spin" size={32} />
        <p>Loading consultation requests...</p>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="consultation-requests-page">
      <div className="page-header">
        <h1>
          <Phone size={28} />
          Consultation Requests
          {pendingCount > 0 && <span className="pending-badge">{pendingCount} pending</span>}
        </h1>
        <p>Manage consultation requests from Credit Control page</p>
      </div>

      <div className="card">
        <div className="filters-bar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name, company, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({requests.length})
            </button>
            <button 
              className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending ({requests.filter(r => r.status === 'pending').length})
            </button>
            <button 
              className={`filter-tab ${filter === 'contacted' ? 'active' : ''}`}
              onClick={() => setFilter('contacted')}
            >
              Contacted ({requests.filter(r => r.status === 'contacted').length})
            </button>
            <button 
              className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed ({requests.filter(r => r.status === 'completed').length})
            </button>
          </div>
        </div>

        {filteredRequests.length > 0 ? (
          <div className="table-container">
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Name & Designation</th>
                  <th>Company & Place</th>
                  <th>Mobile</th>
                  <th>Turnover</th>
                  <th>Amount Stuck</th>
                  <th>Investment</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(request => {
                  const initials = request.name_designation
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);
                  
                  return (
                  <tr key={request.id} className={`status-${request.status}`}>
                    <td>
                      <div className="name-cell">
                        <div className="name-avatar">{initials}</div>
                        <span>{request.name_designation}</span>
                      </div>
                    </td>
                    <td>
                      <div className="company-cell">
                        <Building2 size={18} className="company-icon" />
                        {request.company_place}
                      </div>
                    </td>
                    <td>
                      <div className="phone-cell">
                        <div>
                          <Phone size={14} className="phone-icon" />
                          {request.mobile_number}
                        </div>
                        {request.alternate_mobile && (
                          <div className="alt-phone">{request.alternate_mobile}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="turnover-cell">{request.turnover}</span>
                    </td>
                    <td>
                      <span className="amount-stuck-cell">{request.amount_stuck}</span>
                    </td>
                    <td className="investment-cell">₹{request.investment} Lacs</td>
                    <td className="amount-cell">{request.total_amount || 1500}</td>
                    <td>
                      <div 
                        className={`status-badge-cell status-${request.status}`}
                        onClick={() => setSelectedRequest(request)}
                        title="Click to view details and change status"
                      >
                        {request.status === 'pending' && <Clock size={14} />}
                        {request.status === 'contacted' && <PhoneCall size={14} />}
                        {request.status === 'completed' && <Check size={14} />}
                        {request.status === 'cancelled' && <X size={14} />}
                        {request.status}
                      </div>
                    </td>
                    <td className="date-cell">
                      <span className="date-day">{formatDate(request.created_at).day}</span>
                      <span className="date-time">{formatDate(request.created_at).time}</span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-view"
                          onClick={() => setSelectedRequest(request)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <Phone size={48} />
            <h3>No consultation requests found</h3>
            <p>
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your filters or search term'
                : 'No consultation requests have been submitted yet'}
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Consultation Request Details</h2>
              <button className="close-btn" onClick={() => setSelectedRequest(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-group">
                <label>Name & Designation:</label>
                <p>{selectedRequest.name_designation}</p>
              </div>
              <div className="detail-group">
                <label>Company & Place:</label>
                <p>{selectedRequest.company_place}</p>
              </div>
              <div className="detail-group">
                <label>Mobile Number:</label>
                <p>{selectedRequest.mobile_number}</p>
              </div>
              {selectedRequest.alternate_mobile && (
                <div className="detail-group">
                  <label>Alternate Mobile:</label>
                  <p>{selectedRequest.alternate_mobile}</p>
                </div>
              )}
              <div className="detail-group">
                <label>Expectations:</label>
                <p>{selectedRequest.expectations}</p>
              </div>
              <div className="detail-group">
                <label>Business Turnover:</label>
                <p>{selectedRequest.turnover}</p>
              </div>
              <div className="detail-group">
                <label>Amount Stuck:</label>
                <p>{selectedRequest.amount_stuck}</p>
              </div>
              <div className="detail-group">
                <label>Pain Point:</label>
                <p>{selectedRequest.pain_point}</p>
              </div>
              <div className="detail-group">
                <label>Investment Ready:</label>
                <p>₹{selectedRequest.investment} Lacs</p>
              </div>
              <div className="detail-group">
                <label>Preferred Call Timing:</label>
                <p>{selectedRequest.call_timing}</p>
              </div>
              {selectedRequest.bundles && selectedRequest.bundles.length > 0 && (
                <div className="detail-group">
                  <label>Selected Add-ons:</label>
                  <ul>
                    {selectedRequest.bundles.map((bundle, idx) => (
                      <li key={idx}>{bundle.name} - ₹{bundle.price}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="detail-group">
                <label>Total Amount:</label>
                <p className="amount-highlight">₹{selectedRequest.total_amount || 1500}</p>
              </div>
              <div className="detail-group">
                <label>Submitted At:</label>
                <p>{formatDate(selectedRequest.created_at).day} at {formatDate(selectedRequest.created_at).time}</p>
              </div>
              <div className="detail-group">
                <label>Update Status:</label>
                <select
                  className="status-select-inline"
                  value={selectedRequest.status}
                  onChange={(e) => {
                    handleStatusChange(selectedRequest.id, e.target.value);
                    setSelectedRequest(null);
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationRequests;
